import React from 'react';
import { ago } from '../three/ecosystemState';

// HUD panels for ONE instance's command view, built from deriveCommand()
// state. Pure: no data fetching, no section imports (the drawer map that
// opens the deep sections lives with the page, which owns those
// components). Every number here is the same one the classic sections show.
const usd = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : `$${Number(v).toFixed(2)}`);
const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(v >= 0.999 ? 2 : 1)}%`);
const n = (v) => (v == null ? '—' : Number(v).toLocaleString());
export const Basis = ({ b }) => (b ? <span className={`cmd-basis ${b === 'actual' ? 'is-actual' : ''}`}>{b}</span> : null);
const Row = ({ k, v }) => <span className="cmd-row"><span>{k}</span><span>{v}</span></span>;

export function appPanels(s, { isAdmin = false, hostLabel = null } = {}) {
  const id = s.identity;
  const panels = [];

  panels.push({
    id: 'ap-identity', side: 'left', level: s.core.level === 'off' ? 'gold' : s.core.level, title: 'Instance',
    body: (
      <>
        <strong>{id.name || '—'}</strong>
        <Row k="status" v={id.status || '—'} />
        {id.plan && <Row k="plan" v={id.plan} />}
        {hostLabel && <Row k="host" v={hostLabel} />}
        <Row k="kit" v={<span style={{ color: id.kitDrift ? 'var(--warn)' : undefined }}>{id.version ? String(id.version).slice(0, 8) : id.kitRef || '—'}{id.kitDrift ? ` ≠ ${id.pinnedKitRef}` : ''}</span>} />
        {id.license && <Row k="license" v={`${id.license.status}${id.license.lastSeenAt ? ` · ${ago(id.license.lastSeenAt)}` : ''}`} />}
        {id.prepaidUntil && <Row k="prepaid until" v={new Date(id.prepaidUntil).toLocaleDateString()} />}
      </>
    ),
  });

  panels.push({
    id: 'ap-health', side: 'left', level: s.monitor.level, title: 'Health',
    body: (
      <>
        <strong>{s.monitor.level === 'off' ? 'no data' : s.monitor.level}{s.monitor.latencyMs != null ? <span className="cmd-sub"> · {s.monitor.latencyMs}ms</span> : null}</strong>
        <Row k="uptime 14d" v={pct(s.health.uptime14d)} />
        <Row k="p50 / p95" v={`${s.health.p50 ?? '—'} / ${s.health.p95 ?? '—'} ms`} />
        <Row k="incidents open" v={s.health.incidentsOpen} />
        <Row k="feature checks" v={s.health.features.total ? `${s.health.features.passing}/${s.health.features.total} pass` : 'none'} />
        {s.edge.length > 0 && <Row k="TLS" v={`${Math.min(...s.edge.map((d) => d.tlsDaysLeft ?? 999))}d · ${s.edge.length} domain${s.edge.length > 1 ? 's' : ''}`} />}
        {s.monitor.lastCheckAt ? <span className="cmd-sub">checked {ago(s.monitor.lastCheckAt)}</span> : null}
      </>
    ),
  });

  const down = s.services.filter((x) => x.kind !== 'runner' && x.level !== 'ok' && x.level !== 'off');
  panels.push({
    id: 'ap-services', side: 'left', level: s.services.length ? (down.length ? 'down' : 'ok') : 'off', title: 'Services',
    body: (
      <>
        <strong>{s.services.length ? `${s.services.filter((x) => x.kind !== 'runner' && x.level === 'ok').length}/${s.services.filter((x) => x.kind !== 'runner').length} up` : 'not reported'}</strong>
        {down.slice(0, 4).map((x) => <Row key={x.name} k={x.name} v={x.status} />)}
        {s.memory.available && <Row k="memory (EMA)" v={`${Math.round(s.memory.memMb || 0)} MB${s.memory.share != null ? ` · ${Math.round(s.memory.share * 100)}% of host` : ''}`} />}
        {s.memory.hostVitals && <Row k="host" v={`${s.memory.hostVitals.state}${s.memory.hostVitals.disk != null ? ` · disk ${s.memory.hostVitals.disk}%` : ''}`} />}
      </>
    ),
  });

  if (isAdmin) {
    const c = s.cost;
    panels.push({
      id: 'ap-costs', side: 'right', level: c.available ? c.level : 'off', title: `Costs${id.period ? ` · ${id.period}` : ''}`,
      body: c.available ? (
        <>
          <strong>{usd(c.mtdUsd)}<span className="cmd-sub"> mtd</span></strong>
          <Row k="burn / day" v={usd(c.burnPerDayUsd)} />
          <Row k="projected" v={usd(c.projectedUsd)} />
          {c.limitUsd ? <Row k="limit" v={`${usd(c.limitUsd)} · ${Math.round(c.ratio * 100)}%`} /> : null}
          {c.infra && <Row k={<>infra<Basis b="actual" /></>} v={c.infra.pulledAt ? usd(c.infra.usd) : 'pending · AWS actuals lag ~24h'} />}
          {c.model && c.model.usd > 0 && <Row k={<>models<Basis b="estimated" /></>} v={usd(c.model.usd)} />}
          {c.dev && c.dev.usd > 0 && <Row k={<>dev console<Basis b="actual" /></>} v={usd(c.dev.usd)} />}
          {c.priorUsd != null && <Row k={`${c.priorPeriod || 'prior'} total`} v={usd(c.priorUsd)} />}
        </>
      ) : <span className="cmd-sub">No cost data yet — AWS actuals appear ~24h after tagging.</span>,
    });
  }

  panels.push({
    id: 'ap-security', side: 'right', level: s.security.level, title: 'Security',
    body: (
      <>
        <strong>{s.security.scanning ? 'scanning…' : s.security.lastScanAt ? `${s.security.counts.critical || 0}c ${s.security.counts.high || 0}h ${s.security.counts.medium || 0}m ${s.security.counts.low || 0}l` : s.security.assurance ? 'no scan yet' : 'Assurance add-on'}</strong>
        {s.security.lastScanAt ? <Row k="last scan" v={ago(s.security.lastScanAt)} /> : null}
        {s.security.nextBaselineAt && <Row k="next baseline" v={new Date(s.security.nextBaselineAt).toLocaleDateString()} />}
      </>
    ),
  });

  const lastProd = s.beads.prod[s.beads.prod.length - 1];
  const lastUat = s.beads.uat[s.beads.uat.length - 1];
  panels.push({
    id: 'ap-envs', side: 'right', level: s.twin.exists ? s.twin.level : s.twin.prodLevel, title: 'Environments',
    body: (
      <>
        <strong>{s.twin.deployRunning ? `deploying → ${s.twin.deployRunning}` : s.twin.exists ? 'prod + uat' : 'prod only'}</strong>
        <Row k="prod deploy" v={lastProd ? `${lastProd.status}${lastProd.sha ? ` · ${String(lastProd.sha).slice(0, 7)}` : ''} · ${ago(lastProd.at)}` : '—'} />
        <Row k="uat deploy" v={s.twin.exists ? (lastUat ? `${lastUat.status} · ${ago(lastUat.at)}` : '—') : 'no twin'} />
        <Row k="control plane" v={s.plane.reachable ? (s.plane.updates ? `update ${s.plane.latestKitVersion || ''}` : 'connected') : 'unreachable'} />
      </>
    ),
  });

  const u = s.usage;
  panels.push({
    id: 'ap-usage', side: 'right', level: u.available ? 'gold' : 'off', title: 'Usage',
    body: u.available ? (
      <>
        <strong>{n(u.counters.tokens || 0)}<span className="cmd-sub"> tokens</span></strong>
        {u.bars.filter((b) => b.key !== 'tokens' && b.value > 0).slice(0, 4).map((b) => <Row key={b.key} k={b.label} v={n(b.value)} />)}
        {u.topUsers.slice(0, 2).map((x) => <Row key={x.uid} k={x.uid === 'other' ? 'everyone else' : x.uid} v={n(x.tokens)} />)}
        {u.statement && <Row k="statement draft" v={usd(u.statement.subtotalCents / 100)} />}
        {u.reportedAt ? <span className="cmd-sub">reported {ago(u.reportedAt)}{u.lastActivityAt ? ` · active ${ago(u.lastActivityAt)}` : ''}</span> : null}
      </>
    ) : <span className="cmd-sub">No usage reported yet this month.</span>,
  });

  return panels;
}
